import { roundToFrame } from './time.ts';
import type {
  ProjectSettingsLike,
  RenderClipInput,
  TimelineBuildResult,
} from './types.ts';

function getOutputDimensions(aspectRatio: string | undefined): { width: number; height: number } {
  switch (aspectRatio) {
    case '9:16':
      return { width: 1080, height: 1920 };
    case '4:3':
      return { width: 1440, height: 1080 };
    case '3:4':
      return { width: 1080, height: 1440 };
    case '1:1':
      return { width: 1080, height: 1080 };
    case '16:9':
    default:
      return { width: 1920, height: 1080 };
  }
}

function getFontSizePx(titleFontSize: number | undefined): number {
  return titleFontSize || 40;
}

function mapClipTransition(value: string | undefined): string | undefined {
  if (!value || value === 'none' || value === 'dissolve') return undefined;
  return value;
}

/**
 * Cross-fade overlap. Keep it under the clip length so short trims
 * never leave a gap or a black frame.
 */
export function dissolveOverlapSeconds(clipDuration: number): number {
  const duration = Number(clipDuration) || 0;
  if (duration <= 0.35) return 0;
  const maxOverlap = Math.max(0, duration - 0.2);
  return Math.min(1, Math.max(0.15, duration * 0.25), maxOverlap);
}

function buildTitleCardHtml(
  text: string,
  settings: ProjectSettingsLike,
  width: number,
  height: number,
  hasLogoAbove: boolean,
): string {
  const fontSize = getFontSizePx(settings.title_font_size);
  const valign = hasLogoAbove ? 'bottom' : 'middle';
  const paddingBottom = hasLogoAbove ? Math.round(height * 0.28) : 0;
  return `<table width="${width}" height="${height}" cellpadding="0" cellspacing="0"><tr><td valign="${valign}" align="center" style="padding-bottom: ${paddingBottom}px; padding-left: ${Math.round(width * 0.05)}px; padding-right: ${Math.round(width * 0.05)}px;"><p style="color: white; font-size: ${fontSize}px; font-family: Helvetica, Arial, sans-serif; font-weight: 300; text-align: center;">${text}</p></td></tr></table>`;
}

function getBumperTransition(animation: string | undefined): string | undefined {
  if (animation === 'none') return undefined;
  const map: Record<string, string> = {
    fadeIn: 'fade',
    zoomIn: 'zoom',
    slideUp: 'slideUp',
  };
  return map[animation ?? 'fadeIn'] ?? 'fade';
}

export function clipPlayableLength(clip: RenderClipInput): number {
  return roundToFrame(Math.max(0, clip.trimEnd - clip.trimStart));
}

export function buildShotstackTimeline(
  clips: RenderClipInput[],
  settings: ProjectSettingsLike,
  logoUrl: string | null,
  musicUrl: string | null,
): TimelineBuildResult {
  const { width, height } = getOutputDimensions(settings.aspect_ratio);
  const aspectRatio = settings.aspect_ratio || '16:9';
  const transition = settings.transition_effect || 'dissolve';
  const isDissolve = transition === 'dissolve';
  const isFadeToBlack = transition === 'fade';
  const clipTransition = mapClipTransition(transition);
  const bg = settings.background_color || '#000000';
  const mainClips: Record<string, unknown>[] = [];
  const dissolveVideoTracks: { clips: Record<string, unknown>[] }[] = [];
  const logoOverlayClips: Record<string, unknown>[] = [];
  let trackOffset = 0;
  const logoBumperDuration = settings.logo_bumper_duration || 3;
  const bumperIn = getBumperTransition(settings.logo_bumper_animation);
  const hasOutro = !!(settings.outro_text || (settings.logo_outro_card && logoUrl));

  if (settings.logo_intro_card && logoUrl) {
    mainClips.push({
      asset: { type: 'html', html: '<p>&nbsp;</p>', width, height, background: bg },
      start: trackOffset,
      length: logoBumperDuration,
      transition: { out: transition === 'none' ? undefined : 'fade' },
    });

    const introTransition: Record<string, string> = {};
    if (bumperIn) introTransition.in = bumperIn;
    if (transition !== 'none') introTransition.out = 'fade';
    const introLogoClip: Record<string, unknown> = {
      asset: { type: 'image', src: logoUrl },
      start: trackOffset,
      length: logoBumperDuration,
      fit: 'contain',
      position: 'center',
      scale: 0.45,
      ...(Object.keys(introTransition).length > 0 && { transition: introTransition }),
    };
    if (settings.logo_bumper_tagline) {
      introLogoClip.offset = { x: 0, y: -0.05 };
    }
    logoOverlayClips.push(introLogoClip);

    if (settings.logo_bumper_tagline) {
      const taglineTransition: Record<string, string> = {};
      if (bumperIn) taglineTransition.in = bumperIn;
      if (transition !== 'none') taglineTransition.out = 'fade';
      logoOverlayClips.push({
        asset: {
          type: 'html',
          html: `<table width="${width}" height="${height}" cellpadding="0" cellspacing="0"><tr><td valign="middle" align="center" style="padding-top: ${Math.round(height * 0.2)}px;"><p style="color: #999999; font-size: ${Math.round(width * 0.018)}px; font-family: Helvetica, Arial, sans-serif; font-weight: 300; text-align: center;">${settings.logo_bumper_tagline}</p></td></tr></table>`,
          width,
          height,
        },
        start: trackOffset,
        length: logoBumperDuration,
        ...(Object.keys(taglineTransition).length > 0 && { transition: taglineTransition }),
      });
    }

    trackOffset += logoBumperDuration;
  }

  if (settings.intro_text) {
    const introDuration = 3;
    const hasLogo = !!(settings.logo_on_title_cards && logoUrl);
    mainClips.push({
      asset: {
        type: 'html',
        html: buildTitleCardHtml(settings.intro_text, settings, width, height, hasLogo),
        width,
        height,
        background: bg,
      },
      start: trackOffset,
      length: introDuration,
      transition: { out: transition === 'none' ? undefined : 'fade' },
    });

    if (hasLogo) {
      logoOverlayClips.push({
        asset: { type: 'image', src: logoUrl },
        start: trackOffset,
        length: introDuration,
        fit: 'contain',
        position: 'center',
        scale: 0.35,
        offset: { x: 0, y: -0.15 },
        transition: { out: transition === 'none' ? undefined : 'fade' },
      });
    }

    trackOffset += introDuration;
  }

  const sortedClips = [...clips]
    .filter((clip) => clip.clipUrl && clipPlayableLength(clip) > 0)
    .sort((a, b) => a.order - b.order);
  const videoStart = trackOffset;

  const videoAsset = (clip: RenderClipInput) => {
    const trim = roundToFrame(Math.max(0, clip.trimStart));
    const asset: Record<string, unknown> = {
      type: 'video',
      src: clip.clipUrl,
      volume: 0,
    };
    if (trim > 0) asset.trim = trim;
    return asset;
  };

  if (isDissolve) {
    let start = trackOffset;
    for (let i = 0; i < sortedClips.length; i++) {
      const clip = sortedClips[i];
      const duration = clipPlayableLength(clip);
      const isLast = i === sortedClips.length - 1;
      const overlap = isLast ? 0 : dissolveOverlapSeconds(duration);
      const transitionObj: Record<string, string> = {};
      if (!isLast || hasOutro) transitionObj.out = 'fade';

      const clipObj: Record<string, unknown> = {
        asset: videoAsset(clip),
        start,
        length: duration,
        fit: 'cover',
        ...(Object.keys(transitionObj).length > 0 && { transition: transitionObj }),
      };
      if (settings.filter) clipObj.filter = settings.filter;
      dissolveVideoTracks.push({ clips: [clipObj] });
      start += duration - overlap;
    }
    trackOffset = start;
  } else {
    for (let i = 0; i < sortedClips.length; i++) {
      const clip = sortedClips[i];
      const duration = clipPlayableLength(clip);
      const clipObj: Record<string, unknown> = {
        asset: videoAsset(clip),
        start: trackOffset,
        length: duration,
        fit: 'cover',
      };

      if (isFadeToBlack) {
        const t: Record<string, string> = {};
        if (i > 0 || videoStart > 0) t.in = 'fade';
        if (i < sortedClips.length - 1 || hasOutro) t.out = 'fade';
        if (Object.keys(t).length > 0) clipObj.transition = t;
      } else if (clipTransition && i > 0) {
        clipObj.transition = { in: clipTransition };
      }

      if (settings.filter) clipObj.filter = settings.filter;
      mainClips.push(clipObj);
      trackOffset += duration;
    }
  }

  if (settings.outro_text) {
    const outroDuration = 3;
    const hasLogo = !!(settings.logo_on_title_cards && logoUrl);
    mainClips.push({
      asset: {
        type: 'html',
        html: buildTitleCardHtml(settings.outro_text, settings, width, height, hasLogo),
        width,
        height,
        background: bg,
      },
      start: trackOffset,
      length: outroDuration,
      transition: { in: transition === 'none' ? undefined : 'fade' },
    });

    if (hasLogo) {
      logoOverlayClips.push({
        asset: { type: 'image', src: logoUrl },
        start: trackOffset,
        length: outroDuration,
        fit: 'contain',
        position: 'center',
        scale: 0.35,
        offset: { x: 0, y: -0.15 },
        transition: { in: transition === 'none' ? undefined : 'fade' },
      });
    }

    trackOffset += outroDuration;
  }

  if (settings.logo_outro_card && logoUrl) {
    mainClips.push({
      asset: { type: 'html', html: '<p>&nbsp;</p>', width, height, background: bg },
      start: trackOffset,
      length: logoBumperDuration,
      transition: { in: transition === 'none' ? undefined : 'fade' },
    });

    const outroLogoClip: Record<string, unknown> = {
      asset: { type: 'image', src: logoUrl },
      start: trackOffset,
      length: logoBumperDuration,
      fit: 'contain',
      position: 'center',
      scale: 0.45,
      ...(bumperIn && { transition: { in: bumperIn } }),
    };
    if (settings.logo_bumper_tagline) {
      outroLogoClip.offset = { x: 0, y: -0.05 };
    }
    logoOverlayClips.push(outroLogoClip);

    if (settings.logo_bumper_tagline) {
      logoOverlayClips.push({
        asset: {
          type: 'html',
          html: `<table width="${width}" height="${height}" cellpadding="0" cellspacing="0"><tr><td valign="middle" align="center" style="padding-top: ${Math.round(height * 0.2)}px;"><p style="color: #999999; font-size: ${Math.round(width * 0.018)}px; font-family: Helvetica, Arial, sans-serif; font-weight: 300; text-align: center;">${settings.logo_bumper_tagline}</p></td></tr></table>`,
          width,
          height,
        },
        start: trackOffset,
        length: logoBumperDuration,
        ...(bumperIn && { transition: { in: bumperIn } }),
      });
    }

    trackOffset += logoBumperDuration;
  }

  const timeline: TimelineBuildResult['timeline'] = { background: bg, tracks: [] };

  if (logoOverlayClips.length > 0) {
    timeline.tracks.push({ clips: logoOverlayClips });
  }

  if (logoUrl) {
    const logoBumperIntroLen = settings.logo_intro_card ? logoBumperDuration : 0;
    const introLen = settings.intro_text ? 3 : 0;
    const outroLen = settings.outro_text ? 3 : 0;
    const logoBumperOutroLen = settings.logo_outro_card ? logoBumperDuration : 0;
    const clipsStart = logoBumperIntroLen + introLen;
    const clipsDuration = trackOffset - clipsStart - outroLen - logoBumperOutroLen;

    if (clipsDuration > 0) {
      // fit:contain then scale → logo_scale is % of the video frame (matches UI preview).
      // fit:none scaled against source pixels, so large logos (e.g. 6000px) blew up the frame.
      timeline.tracks.push({
        clips: [{
          asset: { type: 'image', src: logoUrl },
          start: clipsStart,
          length: clipsDuration,
          fit: 'contain',
          position: settings.logo_position || 'bottomRight',
          scale: settings.logo_scale || 0.15,
          opacity: settings.logo_opacity || 0.8,
        }],
      });
    }
  }

  for (const track of dissolveVideoTracks) {
    timeline.tracks.push(track);
  }

  if (mainClips.length > 0) {
    timeline.tracks.push({ clips: mainClips });
  }

  if (musicUrl) {
    const volume = settings.music_volume ?? 0.7;
    const effect = settings.music_effect || 'fadeInFadeOut';
    timeline.tracks.push({
      clips: [{
        asset: {
          type: 'audio',
          src: musicUrl,
          volume,
          effect: effect === 'none' ? undefined : effect,
        },
        start: 0,
        length: trackOffset,
      }],
    });
  }

  return {
    timeline,
    output: {
      format: 'mp4',
      resolution: 'hd',
      aspectRatio,
      size: { width, height },
    },
    totalDuration: trackOffset,
    includedClipCount: sortedClips.length,
  };
}

export function previewNeedsApproximation(settings: ProjectSettingsLike, hasMusic: boolean, hasLogo: boolean): boolean {
  const transition = settings.transition_effect || 'dissolve';
  return (
    hasMusic ||
    hasLogo ||
    !!settings.intro_text ||
    !!settings.outro_text ||
    !!settings.logo_intro_card ||
    !!settings.logo_outro_card ||
    (transition !== 'none')
  );
}
